/**
 * Zone Drawing Layer Component
 *
 * Provides polygon and circle drawing tools for creating watch zones.
 * Uses Leaflet Geoman for drawing functionality.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

export type ZoneShape = 'circle' | 'polygon';

export interface DrawnZone {
  type: ZoneShape;
  // For circle
  center?: { lat: number; lon: number };
  radiusKm?: number;
  // For polygon
  coordinates?: Array<{ lat: number; lon: number }>;
}

interface ZoneDrawingLayerProps {
  enabled: boolean;
  shapeType: ZoneShape;
  onZoneDrawn: (zone: DrawnZone) => void;
  onCancel?: () => void;
  // Initial values for editing
  initialZone?: DrawnZone;
}

export default function ZoneDrawingLayer({
  enabled,
  shapeType,
  onZoneDrawn,
  onCancel,
  initialZone,
}: ZoneDrawingLayerProps) {
  const map = useMap();
  const drawnLayerRef = useRef<L.Layer | null>(null);
  const isDrawingRef = useRef(false);
  const onZoneDrawnRef = useRef(onZoneDrawn);
  onZoneDrawnRef.current = onZoneDrawn;

  // Clear any existing drawn layer
  const clearDrawnLayer = useCallback(() => {
    if (drawnLayerRef.current) {
      map.removeLayer(drawnLayerRef.current);
      drawnLayerRef.current = null;
    }
  }, [map]);

  // Initialize Geoman controls when enabled
  useEffect(() => {
    if (!enabled) {
      // Disable controls and clean up
      map.pm.disableDraw();
      map.pm.removeControls();
      clearDrawnLayer();
      isDrawingRef.current = false;
      map.doubleClickZoom.enable();
      return;
    }

    // While drawing, suppress double-click-to-zoom so the "double-tap to
    // finish" polygon gesture (see the mobile instruction bar) can't also
    // zoom the map — the trickiest step of zone creation. Re-enabled on
    // cleanup below. uplift plan §3.4.
    map.doubleClickZoom.disable();

    // Add Geoman controls with custom styling
    map.pm.addControls({
      position: 'topleft',
      drawCircle: shapeType === 'circle',
      drawPolygon: shapeType === 'polygon',
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawText: false,
      editMode: true,
      dragMode: false,
      cutPolygon: false,
      rotateMode: false,
      removalMode: false,
    });

    // Style the controls
    const controlContainer = document.querySelector('.leaflet-pm-toolbar');
    if (controlContainer) {
      (controlContainer as HTMLElement).style.display = 'flex';
      (controlContainer as HTMLElement).style.flexDirection = 'column';
      (controlContainer as HTMLElement).style.gap = '4px';
    }

    // Customize draw options
    map.pm.setGlobalOptions({
      pathOptions: {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        weight: 3,
      },
      templineStyle: {
        color: '#3b82f6',
        dashArray: '5, 5',
      },
      hintlineStyle: {
        color: '#3b82f6',
        dashArray: '5, 5',
      },
    });

    // If we have an initial zone, draw it
    if (initialZone) {
      if (initialZone.type === 'circle' && initialZone.center && initialZone.radiusKm) {
        const circle = L.circle(
          [initialZone.center.lat, initialZone.center.lon],
          {
            radius: initialZone.radiusKm * 1000,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.15,
            weight: 3,
            pmIgnore: false,
          }
        ).addTo(map);
        drawnLayerRef.current = circle;
        // Enable editing on the existing shape
        circle.pm.enable();
      } else if (initialZone.type === 'polygon' && initialZone.coordinates) {
        const latLngs = initialZone.coordinates.map(c => L.latLng(c.lat, c.lon));
        const polygon = L.polygon(latLngs, {
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          weight: 3,
          pmIgnore: false,
        }).addTo(map);
        drawnLayerRef.current = polygon;
        // Enable editing on the existing shape
        polygon.pm.enable();
      }
    } else {
      // Start drawing mode for new zones
      if (shapeType === 'circle') {
        map.pm.enableDraw('Circle');
      } else {
        map.pm.enableDraw('Polygon');
      }
      isDrawingRef.current = true;
    }

    // Handle shape creation
    const handleCreate = (e: { layer: L.Layer; shape: string }) => {
      // Clear any previous drawn layer
      clearDrawnLayer();
      drawnLayerRef.current = e.layer;
      isDrawingRef.current = false;

      // Extract zone data based on shape type
      if (e.shape === 'Circle') {
        const circle = e.layer as L.Circle;
        const center = circle.getLatLng();
        const radiusM = circle.getRadius();
        onZoneDrawnRef.current({
          type: 'circle',
          center: { lat: center.lat, lon: center.lng },
          radiusKm: radiusM / 1000,
        });
      } else if (e.shape === 'Polygon') {
        const polygon = e.layer as L.Polygon;
        const latLngs = polygon.getLatLngs()[0] as L.LatLng[];
        const coordinates = latLngs.map(ll => ({ lat: ll.lat, lon: ll.lng }));
        onZoneDrawnRef.current({
          type: 'polygon',
          coordinates,
        });
      }
    };

    // Handle shape editing
    const handleEdit = (e: { layer: L.Layer; shape?: string }) => {
      const layer = e.layer;
      if (layer instanceof L.Circle) {
        const center = layer.getLatLng();
        const radiusM = layer.getRadius();
        onZoneDrawnRef.current({
          type: 'circle',
          center: { lat: center.lat, lon: center.lng },
          radiusKm: radiusM / 1000,
        });
      } else if (layer instanceof L.Polygon) {
        const latLngs = layer.getLatLngs()[0] as L.LatLng[];
        const coordinates = latLngs.map(ll => ({ lat: ll.lat, lon: ll.lng }));
        onZoneDrawnRef.current({
          type: 'polygon',
          coordinates,
        });
      }
    };

    map.on('pm:create', handleCreate);
    map.on('pm:edit', handleEdit);

    // Handle escape key to cancel
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        map.pm.disableDraw();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('pm:create', handleCreate);
      map.off('pm:edit', handleEdit);
      document.removeEventListener('keydown', handleKeyDown);
      map.pm.disableDraw();
      map.pm.removeControls();
      // Restore double-click-to-zoom when leaving draw mode.
      map.doubleClickZoom.enable();
    };
  }, [enabled, shapeType, map, initialZone, clearDrawnLayer, onCancel]);

  return null;
}
